import posthog from "posthog-js"

/**
 * Every browser tab opens its own XMPP connection. Too many tabs means too many
 * connections for one user, which our auto-reconnect makes worse. We cap the
 * number of tabs that are allowed to connect by handing out a fixed number of
 * "slots".
 *
 * A slot is a Web Lock. The browser releases the lock automatically when the tab
 * is closed or crashes, so there is nothing to clean up and no stale state.
 *
 * Limitations: locks are scoped to one browser profile and one origin, so other
 * browsers, incognito windows and phones are not counted.
 */
export const MAX_CONCURRENT_TABS = 5

const SLOT_LOCK_PREFIX = "slickshift-xmpp-tab-slot-"

/**
 * The limit only applies when this PostHog flag is on, so it can be rolled out
 * per vhost. A tab that starts before the flags have loaded is not limited;
 * posthog-js caches flags in localStorage, so this only affects the very first
 * page load on a device.
 */
const TAB_LIMIT_FLAG = "max-tabs-limit"

let heldSlot: number | null = null
let releaseHeldSlot: (() => void) | null = null

const hasWebLocks = () =>
  typeof navigator !== "undefined" &&
  typeof navigator.locks?.request === "function"

export const isTabLimitEnabled = () => {
  if (window.ReactNativeWebView) return false
  if (!hasWebLocks()) return false
  return posthog.isFeatureEnabled(TAB_LIMIT_FLAG) === true
}

export const getHeldTabSlot = () => heldSlot

const tryAcquireSlot = (index: number): Promise<boolean> =>
  new Promise((resolve, reject) => {
    // The lock is held for as long as the callback's promise is pending, so we
    // return a promise that only settles when releaseTabSlot() is called.
    navigator.locks
      .request(
        `${SLOT_LOCK_PREFIX}${index}`,
        { mode: "exclusive", ifAvailable: true },
        (lock) => {
          if (!lock) {
            resolve(false)
            return
          }

          return new Promise<void>((release) => {
            heldSlot = index
            releaseHeldSlot = () => {
              heldSlot = null
              releaseHeldSlot = null
              release()
            }
            resolve(true)
          })
        },
      )
      .catch(reject)
  })

/**
 * Takes a connection slot for this tab, if one is free.
 * Returns false only when all slots are taken by other tabs.
 * Fails open: any unexpected error or missing browser support returns true.
 */
export const acquireTabSlot = async (): Promise<boolean> => {
  if (heldSlot !== null) return true
  if (!isTabLimitEnabled()) return true

  for (let index = 1; index <= MAX_CONCURRENT_TABS; index++) {
    try {
      if (await tryAcquireSlot(index)) {
        return true
      }
    } catch {
      return true
    }
  }

  return false
}

/** Gives the slot back. The browser does this on its own when the tab closes. */
export const releaseTabSlot = () => {
  releaseHeldSlot?.()
}
